import {
  queryKeys,
  useHttpClient,
  useActiveOrganizationId,
} from '@repo/api-client/react';
import {
  RemindInvoiceResponseSchema,
  type InvoiceReminderTone,
  type RemindInvoiceResponse,
} from '@repo/api-client/dtos';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postJSON } from '../internal/http';

export {
  InvoiceReminderToneSchema,
  RemindInvoiceRequestSchema,
  RemindInvoiceResponseSchema,
  type InvoiceReminderTone,
  type RemindInvoiceRequest,
  type RemindInvoiceResponse,
} from '@repo/api-client/dtos';

export interface RemindInvoiceVariables {
  invoiceId: string;
  /** Operator-selected tone — picks the email kicker + opening copy. */
  tone: InvoiceReminderTone;
  /** Optional sender note that lands in the reminder body. */
  message?: string;
  /** Optional override for the client's on-file email. */
  recipient?: string;
}

/**
 * Drive the "Send reminder" action.
 *
 * Distinct from `useSendInvoice`: that hook re-issues the original
 * invoice email (template `InvoiceSentEmail`, subject "INV-007 from
 * Acme — $1,840.00", transitions draft → sent). `useRemindInvoice`
 * is the follow-up action — it renders `InvoiceReminderEmail`,
 * ships a "Reminder: …" subject, and does NOT change invoice
 * status. The bug we're fixing here was the AI reminder card calling
 * `useSendInvoice`; the recipient saw a duplicate "New invoice"
 * email with the AI-drafted reminder body smuggled in as the
 * optional sender note.
 *
 * On success we:
 *   1. Write the freshly-returned invoice into the detail cache
 *      (status is unchanged, but newer fields like `updatedAt`
 *      should reflect the activity write).
 *   2. Invalidate the activity feed so the new
 *      `email.reminder.sent` row appears immediately.
 *   3. Invalidate the list cache (cheap; keeps any
 *      "last-touched-at" sort honest).
 *
 * Errors propagate untouched so the calling screen can surface the
 * server's actual reason — `ConflictError` for the 60s replay
 * window or invalid status, `ValidationError` for missing client
 * email — through the standard `NoticeSheet` pattern.
 */
export function useRemindInvoice() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: ({ invoiceId, tone, message, recipient }: RemindInvoiceVariables) => {
      if (!orgId) throw new Error('No active organization');
      return postJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/remind`,
        {
          tone,
          ...(message ? { message } : {}),
          ...(recipient ? { recipient } : {}),
        },
        RemindInvoiceResponseSchema,
      );
    },
    onSuccess: (response) => {
      const { invoice } = response;
      queryClient.setQueryData(
        queryKeys.invoices.detail(invoice.orgId, invoice.id),
        invoice,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.all(invoice.orgId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.activities(invoice.orgId, invoice.id),
      });
    },
  });

  return {
    sendReminder: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
